import type { EventsHubCard, LiveProgressParts } from '../types/events-hub'
import type { EventsPortalPlace } from '../types/events-management'

const MS_PER_DAY = 24 * 60 * 60 * 1000

/*
 * `timeZone` reaches these from the API, so an unknown IANA id makes `Intl.DateTimeFormat` throw.
 * Both fall back to the viewer's own zone rather than taking the card down with them, matching
 * `formatEventTimeLabel` and `toUtcCalendarDay` below.
 */
const formatDay = (value: Date, timeZone?: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    }).format(value)
  } catch {
    if (!timeZone) return ''
    return formatDay(value)
  }
}

const pad = (value: number): string => String(value).padStart(2, '0')

interface WallParts {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/** Calendar and clock fields of an instant as seen in `timeZone` (the device zone when omitted). */
const wallParts = (date: Date, timeZone?: string): WallParts | undefined => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date)
    const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find(part => part.type === type)?.value ?? ''
    const hour = get('hour')
    const wall = {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: hour === '24' ? 0 : Number(hour),
      minute: Number(get('minute')),
      second: Number(get('second')),
    }
    return wall.year && wall.month && wall.day ? wall : undefined
  } catch {
    return undefined
  }
}

const calendarDayKey = (value: Date, timeZone?: string): string => {
  const wall = wallParts(value, timeZone)
  return wall ? `${wall.year}-${pad(wall.month)}-${pad(wall.day)}` : ''
}

const toUtcCalendarDay = (value: Date, timeZone?: string): number | undefined => {
  const wall = wallParts(value, timeZone)
  return wall ? Date.UTC(wall.year, wall.month - 1, wall.day) : undefined
}

export const isOnlineEventLocation = (location: EventsPortalPlace | undefined): boolean => {
  const type = location?.type?.toLowerCase()
  return type === 'online' || type === 'virtual'
}

export const resolveEventDisplayTimeZone = (location: EventsPortalPlace | undefined, eventTimeZone?: string): string | undefined =>
  isOnlineEventLocation(location) ? undefined : eventTimeZone

export const formatEventDateLabel = (startIso: string, endIso: string, timeZone?: string): string => {
  const start = new Date(startIso)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(endIso)
  if (Number.isNaN(end.getTime()) || calendarDayKey(start, timeZone) === calendarDayKey(end, timeZone)) {
    return formatDay(start, timeZone)
  }

  const startLabel = formatDay(start, timeZone)
  const endLabel = formatDay(end, timeZone)
  const startMonthYear = startLabel.replace(/\s+\d+,/, '')
  const endMonthYear = endLabel.replace(/\s+\d+,/, '')
  const startYear = startLabel.replace(/.*,\s*/, '')
  const endYear = endLabel.replace(/.*,\s*/, '')
  if (startMonthYear === endMonthYear) {
    const startDay = startLabel.replace(/^[A-Za-z]+\s+/, '').replace(/,.*/, '')
    const endDay = endLabel.replace(/^[A-Za-z]+\s+/, '').replace(/,.*/, '')
    const month = startLabel.replace(/\s+\d+,.*/, '')
    return `${month} ${startDay}–${endDay}, ${startYear}`
  }
  if (startYear === endYear) {
    const startWithoutYear = startLabel.replace(/,\s*\d{4}$/, '')
    const endWithoutYear = endLabel.replace(/,\s*\d{4}$/, '')
    return `${startWithoutYear} – ${endWithoutYear}, ${startYear}`
  }
  return `${startLabel} – ${endLabel}`
}

/** `onlineLabel` is the localised label for online events — the caller owns i18n. */
export const toEventLocationLabel = (location: EventsPortalPlace | undefined, onlineLabel: string): string => {
  if (!location) return ''
  if (isOnlineEventLocation(location)) return onlineLabel

  const city = location.city ?? ''
  const region = location.state || location.country || ''
  if (city && region && region !== city) return `${city}, ${region}`
  return city || location.venue || location.address || ''
}

/** About-page location card. Listing cards keep `toEventLocationLabel` (city and region). */
export const toEventAboutLocationLabel = (location: EventsPortalPlace | undefined, onlineLabel: string): string => {
  if (!location) return ''
  if (isOnlineEventLocation(location)) return onlineLabel

  const venue = location.venue?.trim() ?? ''
  const city = location.city?.trim() ?? ''
  if (venue && city && venue !== city) return `${venue}, ${city}`
  return venue || city || location.address?.trim() || location.formattedAddress?.trim() || ''
}

/** Offset token at `date`, in `timeZone` or the device zone when omitted. */
const timeZoneOffsetToken = (date: Date, timeZone?: string): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        timeZoneName: 'shortOffset',
        ...(timeZone ? { timeZone } : {}),
      })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName')?.value ?? ''
    )
  } catch {
    return ''
  }
}

/** The abbreviation is only useful when the event clock is not already the device clock. */
const shouldShowTimeZoneName = (date: Date, timeZone?: string): boolean => {
  const zone = timeZone?.trim()
  if (!zone) return false
  const eventOffset = timeZoneOffsetToken(date, zone)
  const deviceOffset = timeZoneOffsetToken(date)
  if (!eventOffset || !deviceOffset) return true
  return eventOffset !== deviceOffset
}

export const formatEventTimeLabel = (startIso: string, timeZone?: string, displayFormat: '12h' | '24h' = '12h'): string => {
  const date = new Date(startIso)
  if (Number.isNaN(date.getTime())) return ''

  const options: Intl.DateTimeFormatOptions = {
    hour: displayFormat === '24h' ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: displayFormat !== '24h',
    ...(shouldShowTimeZoneName(date, timeZone) ? { timeZoneName: 'short' as const } : {}),
    ...(timeZone ? { timeZone } : {}),
  }

  try {
    return new Intl.DateTimeFormat('en-US', options).format(date)
  } catch {
    if (!timeZone) return ''
    return formatEventTimeLabel(startIso, undefined, displayFormat)
  }
}

export const formatEventWeekdayTimeLabel = (startIso: string, timeZone?: string, displayFormat: '12h' | '24h' = '12h'): string => {
  const date = new Date(startIso)
  if (Number.isNaN(date.getTime())) return ''
  const time = formatEventTimeLabel(startIso, timeZone, displayFormat)
  if (!time) return ''

  try {
    const weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      ...(timeZone ? { timeZone } : {}),
    }).format(date)
    return `${weekday}, ${time}`
  } catch {
    if (!timeZone) return ''
    return formatEventWeekdayTimeLabel(startIso, undefined, displayFormat)
  }
}

export const formatEventDateChip = (startIso: string, endIso: string, timeZone?: string): string =>
  formatEventDateLabel(startIso, endIso, timeZone)
    .replace(/,\s*\d{4}/g, '')
    .trim()

export const toEventListingFields = (
  startIso: string,
  endIso: string,
  timeZone?: string
): Partial<Pick<EventsHubCard, 'startAt' | 'endAt' | 'timeLabel' | 'dateChip' | 'timeZone'>> => {
  const timeLabel = formatEventWeekdayTimeLabel(startIso, timeZone)
  const dateChip = formatEventDateChip(startIso, endIso, timeZone)
  return {
    ...(startIso ? { startAt: startIso } : {}),
    ...(endIso ? { endAt: endIso } : {}),
    ...(timeLabel ? { timeLabel } : {}),
    ...(dateChip ? { dateChip } : {}),
    ...(timeZone ? { timeZone } : {}),
  }
}

export const getLiveProgressParts = (startIso: string, endIso: string, now: Date, timeZone?: string): LiveProgressParts | undefined => {
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return undefined

  const startDay = toUtcCalendarDay(start, timeZone)
  const endDay = toUtcCalendarDay(end, timeZone)
  const nowDay = toUtcCalendarDay(now, timeZone)
  if (startDay === undefined || endDay === undefined || nowDay === undefined) return undefined

  const total = Math.round((endDay - startDay) / MS_PER_DAY) + 1
  if (total >= 2) {
    const current = Math.min(total, Math.max(1, Math.round((nowDay - startDay) / MS_PER_DAY) + 1))
    return { type: 'day', current, total }
  }

  const minutes = Math.max(0, Math.round((end.getTime() - now.getTime()) / 60000))
  const hours = Math.floor(minutes / 60)
  return { type: 'remaining', hours, minutes: hours > 0 ? minutes % 60 : minutes }
}

export const parseClock = (value: string): { hours: number; minutes: number } | undefined => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) return undefined
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return undefined
  return { hours, minutes }
}

const offsetMs = (instant: Date, timeZone: string): number | undefined => {
  const wall = wallParts(instant, timeZone)
  if (!wall) return undefined
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second) - instant.getTime()
}

const parseDay = (day: string): { year: number; month: number; date: number } | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!match) return undefined
  const year = Number(match[1])
  const month = Number(match[2])
  const date = Number(match[3])
  if (month < 1 || month > 12 || date < 1 || date > 31) return undefined
  return { year, month, date }
}

export const isoToZonedWall = (iso: string, timeZone?: string): { day: string; hours: number; minutes: number } | undefined => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  const wall = wallParts(date, timeZone)
  if (!wall) return undefined
  return { day: `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`, hours: wall.hour, minutes: wall.minute }
}

/** Converts a `YYYY-MM-DD` day plus an `HH:mm` wall clock in `timeZone` to an ISO instant. */
export const zonedClockToIso = (day: string, clock: string, timeZone?: string): string | undefined => {
  const parsedDay = parseDay(day)
  const parsedClock = parseClock(clock)
  if (!parsedDay || !parsedClock) return undefined
  const wallAsUtc = Date.UTC(parsedDay.year, parsedDay.month - 1, parsedDay.date, parsedClock.hours, parsedClock.minutes, 0)
  if (!timeZone) return new Date(wallAsUtc).toISOString()
  const firstOffset = offsetMs(new Date(wallAsUtc), timeZone)
  if (firstOffset === undefined) return undefined
  const secondOffset = offsetMs(new Date(wallAsUtc - firstOffset), timeZone)
  if (secondOffset === undefined) return undefined
  return new Date(wallAsUtc - secondOffset).toISOString()
}

export const addMinutesToIso = (iso: string, minutes: number): string | undefined => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime()) || !Number.isFinite(minutes)) return undefined
  return new Date(date.getTime() + minutes * 60 * 1000).toISOString()
}
